/**
 * Promise-based themed confirm — a drop-in async replacement for the native,
 * thread-blocking `window.confirm`.
 *
 * The native dialog halts the renderer until the user answers, which (a) freezes
 * the page under CDP/automation-driven testing (30s timeouts, "deletion
 * impossible") and (b) is unstyled/inconsistent with the rest of the app, which
 * already uses themed Modal confirms. Mount <GlobalConfirmHost/> once at the app
 * root; then anywhere:
 *
 *   if (!(await confirmAsync('Supprimer « … » ?'))) return;
 *
 * Falls back to the native confirm if the host isn't mounted (never blocks a
 * destructive action outright).
 */
import { useEffect, useState } from 'react';

import { Modal } from './Modal';

export interface ConfirmOptions {
  title?: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Style the confirm as destructive (danger). Defaults to true. */
  destructive?: boolean;
}

interface PendingConfirm extends ConfirmOptions {
  id: number;
  resolve: (ok: boolean) => void;
}

let emit: ((c: PendingConfirm) => void) | null = null;
let nextId = 0;

/** Show a themed confirm dialog; resolves true on confirm, false on cancel. */
export function confirmAsync(opts: ConfirmOptions | string): Promise<boolean> {
  const options: ConfirmOptions = typeof opts === 'string' ? { message: opts } : opts;
  return new Promise<boolean>((resolve) => {
    if (!emit) {
      // Host not mounted (e.g. a non-shell page) — fall back so the action is
      // never silently blocked.
      const text = typeof options.message === 'string' ? options.message : 'Confirmer ?';
      resolve(window.confirm(text));
      return;
    }
    nextId += 1;
    emit({ ...options, id: nextId, resolve });
  });
}

const cancelBtn =
  'rounded-token border border-border px-4 py-2 text-sm font-medium text-foreground transition hover:bg-surface-2 disabled:opacity-50';
const dangerBtn =
  'rounded-token bg-danger px-4 py-2 text-sm font-semibold text-white transition hover:bg-danger/90 disabled:opacity-50';
const primaryBtn =
  'rounded-token bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground transition hover:bg-primary/90 disabled:opacity-50';

/** Mount once at the app root. Renders the active confirm dialog, if any. */
export function GlobalConfirmHost() {
  const [pending, setPending] = useState<PendingConfirm | null>(null);
  useEffect(() => {
    emit = setPending;
    return () => {
      emit = null;
    };
  }, []);

  if (!pending) return null;

  const close = (ok: boolean) => {
    pending.resolve(ok);
    setPending(null);
  };
  const destructive = pending.destructive !== false;

  return (
    <Modal
      title={pending.title ?? 'Confirmation'}
      onClose={() => close(false)}
      size="sm"
      footer={
        <>
          <button type="button" className={cancelBtn} onClick={() => close(false)}>
            {pending.cancelLabel ?? 'Annuler'}
          </button>
          <button
            type="button"
            className={destructive ? dangerBtn : primaryBtn}
            onClick={() => close(true)}
          >
            {pending.confirmLabel ?? 'Confirmer'}
          </button>
        </>
      }
    >
      <p className="text-sm text-foreground">{pending.message}</p>
    </Modal>
  );
}
